"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  id: 1,
  name: "NEW YORK CENTER",
  address: "123 Precision Way, Manhattan, NY 10001",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/image_m61rvlsi.png",
  link: "/DedicatedServiceAndLocationPagesPage"
}, {
  id: 2,
  name: "CHICAGO HUB",
  address: "456 Industrial Blvd, Chicago, IL 60607",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_riverside_estate.png",
  link: "/DedicatedServiceAndLocationPage"
}, {
  id: 3,
  name: "LOS ANGELES BASE",
  address: "789 Tech Parkway, Los Angeles, CA 90001",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_highland_retreat.png",
  link: "/TrustedInspectionsServiceAndLocationPagesPage"
}];
export default function OurLocations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="85225" id="location-overview" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="text-4xl font-bold tracking-tight text-secondary mb-6">
            Location Overview
          </h2>
          <Separator className="mb-12 bg-primary h-px w-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((loc, i) => <LocationCard key={loc.id} location={loc} index={i} />)}
        </div>
      </div>
    </section>;
}
function LocationCard({
  location,
  index
}: {
  location: (typeof locations)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full overflow-hidden border-border/50 shadow-sm hover:shadow-lg transition-shadow duration-300 bg-card flex flex-col rounded-xl">
        <div className="relative h-64 w-full shrink-0">
          <img src={location.image} alt={location.name} className="object-cover w-full h-full" />
          <div className="absolute inset-0 bg-secondary/20 transition-opacity duration-300 hover:bg-secondary/10" />
        </div>
        <CardContent className="px-6 pb-6 pt-6 flex-grow flex flex-col items-start text-left">
          <h3 className="text-2xl font-bold text-secondary mb-3 tracking-tight uppercase border-l-2 border-primary pl-4">
            {location.name}
          </h3>
          <p className="text-muted-foreground mb-8 flex-grow text-base font-normal">
            {location.address}
          </p>
          <Button asChild className="w-full bg-secondary text-primary hover:bg-secondary/90 rounded-none h-12 text-base font-medium shadow-none transition-colors duration-200">
            <Link to={location.link}>
              <MapPin className="mr-2 h-5 w-5 text-primary" />
              Get Directions
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>;
}